import { useCallback, useEffect, useMemo, useState } from 'react';
import { errorMessage, useLocale, type MessageKey } from '../../i18n';
import { Field, StatusMessage } from '../ui';
import { ConfirmButton } from '../ui/ConfirmButton';
import {
  correctRating as defaultCorrect,
  loadRatingData as defaultLoad,
  MIN_REASON_LENGTH,
  type CorrectionResult,
  type RatingAdjustment,
  type RatingDataResult,
  type RatingPlayer,
} from '../../lib/api/ratingCorrection';

// ─────────────────────────────────────────────────────────────────────
// /sa → Spillere → Ratingkorrigering (#793).
//
// Setting a rating by hand became PLATFORM authority in #197: the player table
// has no tenant column, so a correction is a correction for every club, and
// the club screens that offered it lost the control. This is the other half of
// that decision — the place StarPoint does it, so «our new player was entered
// at 1200 instead of 1700» has an answer that is in the product and audited
// rather than a developer with a database connection.
//
// WHAT THE SCREEN DOES NOT DO: decide anything about ratings. No bounds, no
// recomputed level, no arithmetic. The server derives the old value and the
// change from the row it holds, under the rating lock, and writes the audit
// row in the same transaction; this sends a new value and a reason and
// renders the answer. The ELO maths are engine semantics and are not touched.
//
// WHO SEES WHAT. Reading the player list and the correction log is open to
// any platform role (the API's `/players` floor admits them), and support
// needs exactly that to answer «why did this rating change». The correction
// itself is offered only when `canWrite` — see `SuperadminRoute` for how the
// panel decides that, and why it is a courtesy: the route refuses anyone
// without `rating.adjust_manual` at platform scope whatever renders here.
//
// A CONFIRMATION, ALWAYS. The button arms a question first (`ConfirmButton`,
// never `window.confirm()` — DOMAIN_RULES §9), because the blast radius of
// this button is every club the player has ever played in.
// ─────────────────────────────────────────────────────────────────────

export interface RatingCorrectionSectionProps {
  /** Does the viewer hold the correction right, per the SERVER's answer? */
  canWrite: boolean;
  /** Injectable for tests; defaults to the real client. */
  load?: () => Promise<RatingDataResult>;
  /** Injectable for tests; defaults to the real client. */
  correct?: (id: string | number, newElo: number, reason: string) => Promise<CorrectionResult>;
}

/** How many matches a search lists before asking for a narrower one. */
const MAX_RESULTS = 25;
/** A search shorter than this lists nothing — this is not a directory dump. */
const MIN_QUERY = 2;

type Outcome = { kind: 'success' | 'info' | 'error'; text: string } | null;

const sameId = (a: string | number, b: string | number) => String(a) === String(b);

export function RatingCorrectionSection({
  canWrite,
  load = defaultLoad,
  correct = defaultCorrect,
}: RatingCorrectionSectionProps) {
  const { t, formatDate } = useLocale();
  const [data, setData] = useState<RatingDataResult | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newElo, setNewElo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  const reload = useCallback(async () => {
    setData(null);
    setData(await load());
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await load();
      if (!cancelled) setData(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // A STABLE empty list while nothing is loaded, so the memos below do not
  // recompute on every render for want of an answer.
  const players = useMemo(() => (data?.state === 'ok' ? data.players : []), [data]);
  const adjustments = useMemo(() => (data?.state === 'ok' ? data.adjustments : []), [data]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (q.length < MIN_QUERY) return [];
    return players.filter(
      (p) =>
        String(p.name ?? '')
          .toLowerCase()
          .includes(q) || String(p.id).toLowerCase() === q,
    );
  }, [players, q]);

  const selected = selectedId != null ? players.find((p) => sameId(p.id, selectedId)) : undefined;
  const history = useMemo(
    () =>
      selected
        ? adjustments
            .filter((a) => sameId(a.playerId, selected.id))
            .sort((a, b) => b.adjustedAt - a.adjustedAt)
        : [],
    [adjustments, selected],
  );

  const parsedElo = newElo.trim() === '' ? NaN : Number(newElo);
  const eloValid = Number.isFinite(parsedElo);
  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;

  const choose = (p: RatingPlayer) => {
    setSelectedId(String(p.id));
    setNewElo('');
    setReason('');
    setOutcome(null);
  };

  const refusalText = (status: number, code: string | null, minimum?: number): string => {
    const known: Record<string, MessageKey> = {
      reason_required: 'saRating.error.reasonRequired',
      not_found: 'saRating.error.notFound',
      forbidden: 'saRating.error.forbidden',
      reauthentication_required: 'saRating.error.reauthenticate',
    };
    if (code && known[code])
      return t(known[code], { minimum: String(minimum ?? MIN_REASON_LENGTH) });
    // `newElo is required` is the route's only other 400, and it is a sentence
    // rather than a code — so it is recognised by status, not matched as text.
    if (status === 400) return t('saRating.error.invalidRating');
    return errorMessage(t, code);
  };

  const submit = async () => {
    if (!selected || !eloValid || !reasonValid) return;
    const target = selected;
    setBusy(true);
    setOutcome(null);
    const result = await correct(target.id, parsedElo, reason.trim());
    setBusy(false);

    if (result.state === 'ok') {
      // The SERVER's numbers, not the form's: what it stored and what it logged.
      setData((prev) =>
        prev?.state === 'ok'
          ? {
              ...prev,
              players: prev.players.map((p) =>
                sameId(p.id, target.id) ? { ...p, ...result.player } : p,
              ),
              adjustments: [...prev.adjustments, result.adjustment],
            }
          : prev,
      );
      setNewElo('');
      setReason('');
      setOutcome({
        kind: 'success',
        text: t('saRating.outcome.saved', {
          name: target.name,
          from: String(result.adjustment.oldElo),
          to: String(result.adjustment.newElo),
        }),
      });
      return;
    }

    if (result.state === 'refused' && result.code === 'no_change') {
      // A REAL OUTCOME, NOT AN ERROR (#793). The stored rating already IS the
      // value asked for — perhaps another operator got there first — so the
      // row the screen shows is brought up to what the server just said.
      setData((prev) =>
        prev?.state === 'ok'
          ? {
              ...prev,
              players: prev.players.map((p) =>
                sameId(p.id, target.id) ? { ...p, elo: parsedElo } : p,
              ),
            }
          : prev,
      );
      setOutcome({
        kind: 'info',
        text: t('saRating.outcome.noChange', { elo: String(parsedElo) }),
      });
      return;
    }

    if (result.state === 'refused') {
      setOutcome({ kind: 'error', text: refusalText(result.status, result.code, result.minimum) });
      return;
    }
    if (result.state === 'no-api') {
      setOutcome({ kind: 'error', text: t('saRating.load.noApi') });
      return;
    }
    // NO ANSWER IS NOT «NOTHING HAPPENED». The request may have landed and
    // the reply been lost, so the instruction is to look before trying again.
    // (Trying again is safe either way: the same value a second time is
    // `no_change`, not a second correction.)
    //
    // AND NEITHER IS A 5xx (#944 review). The route commits the rating, the
    // adjustment row and the audit row in one transaction and only THEN builds
    // the response, so an error after the commit — in serialisation, a
    // middleware, the platform — still arrives as a 5xx. A transport status
    // cannot say whether the transaction committed; the screen must not either.
    setOutcome({
      kind: 'error',
      text: result.status ? t('saRating.error.server') : t('saRating.error.noAnswer'),
    });
  };

  return (
    <section className="card p-6" data-testid="sa-rating">
      <h1 className="font-display text-2xl font-semibold text-ink">{t('saRating.title')}</h1>
      <p className="text-sm text-ink-3 mt-1">{t('saRating.lead')}</p>
      {data?.state === 'ok' && (
        <button
          type="button"
          className="btn-ghost text-[11px] mt-2"
          onClick={() => void reload()}
          data-testid="sa-rating-reload"
        >
          {t('saRating.load.reload')}
        </button>
      )}

      {data === null && (
        <p className="text-[11px] text-ink-3 mt-4" data-testid="sa-rating-state">
          {t('saRating.load.loading')}
        </p>
      )}
      {data && data.state !== 'ok' && (
        <div className="mt-4" data-testid="sa-rating-state">
          <StatusMessage kind={data.state === 'no-api' ? 'info' : 'error'}>
            {data.state === 'no-api'
              ? t('saRating.load.noApi')
              : data.state === 'refused'
                ? data.code === 'reauthentication_required'
                  ? t('saRating.error.reauthenticate')
                  : t('saRating.load.refused')
                : t('saRating.load.failed')}
          </StatusMessage>
          {data.state !== 'no-api' && (
            <button type="button" className="btn-ghost text-xs mt-2" onClick={() => void reload()}>
              {t('saRating.load.retry')}
            </button>
          )}
        </div>
      )}

      {data?.state === 'ok' && (
        <div className="mt-5 grid gap-6 md:grid-cols-[minmax(0,18rem)_1fr]">
          <div>
            <Field label={t('saRating.search.label')} hint={t('saRating.search.hint')}>
              <input
                type="search"
                className="input w-full text-xs"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="sa-rating-search"
              />
            </Field>
            {q.length >= MIN_QUERY && matches.length === 0 && (
              <p className="text-[11px] text-ink-3 mt-3" data-testid="sa-rating-no-match">
                {t('saRating.search.noMatch')}
              </p>
            )}
            {matches.length > 0 && (
              <ul
                className="mt-3 space-y-0.5"
                data-testid="sa-rating-results"
                aria-label={t('saRating.search.results')}
              >
                {matches.slice(0, MAX_RESULTS).map((p) => {
                  const current = selected != null && sameId(p.id, selected.id);
                  return (
                    <li key={String(p.id)}>
                      <button
                        type="button"
                        onClick={() => choose(p)}
                        aria-current={current ? 'true' : undefined}
                        className={`w-full text-left rounded px-2 py-1.5 text-xs ${
                          current ? 'bg-white/10 font-medium' : 'hover:bg-white/5'
                        }`}
                        data-testid={`sa-rating-result-${String(p.id)}`}
                      >
                        <span className="block">{p.name}</span>
                        <span className="block font-mono text-[10px] text-ink-3">
                          {t('saRating.search.resultMeta', {
                            id: String(p.id),
                            elo: p.elo != null ? String(p.elo) : '—',
                          })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {matches.length > MAX_RESULTS && (
              <p className="text-[11px] text-ink-3 mt-2" data-testid="sa-rating-more">
                {t('saRating.search.more', { count: String(matches.length - MAX_RESULTS) })}
              </p>
            )}
          </div>

          <div className="min-w-0">
            {!selected && (
              <p className="text-[11px] text-ink-3" data-testid="sa-rating-none">
                {t('saRating.player.none')}
              </p>
            )}
            {selected && (
              <div data-testid="sa-rating-player">
                <h2 className="font-display text-lg font-semibold">{selected.name}</h2>
                <p className="font-mono text-[10px] text-ink-3">
                  {t('saRating.player.id', { id: String(selected.id) })}
                </p>
                <dl className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-ink-3">
                      {t('saRating.player.rating')}
                    </dt>
                    <dd className="font-mono text-base" data-testid="sa-rating-current">
                      {selected.elo != null ? String(selected.elo) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-ink-3">
                      {t('saRating.player.level')}
                    </dt>
                    <dd className="font-mono text-base" data-testid="sa-rating-level">
                      {selected.level != null ? String(selected.level) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-ink-3">
                      {t('saRating.player.matches')}
                    </dt>
                    <dd className="font-mono text-base">
                      {selected.matchesPlayed != null ? String(selected.matchesPlayed) : '—'}
                    </dd>
                  </div>
                </dl>

                <h3 className="font-display text-sm font-semibold mt-6">
                  {t('saRating.history.title')}
                </h3>
                {history.length === 0 ? (
                  <p className="text-[11px] text-ink-3 mt-1" data-testid="sa-rating-history-empty">
                    {t('saRating.history.empty')}
                  </p>
                ) : (
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-xs" data-testid="sa-rating-history">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
                          <th scope="col" className="py-1 pr-3 font-medium">
                            {t('saRating.history.when')}
                          </th>
                          <th scope="col" className="py-1 pr-3 font-medium">
                            {t('saRating.history.from')}
                          </th>
                          <th scope="col" className="py-1 pr-3 font-medium">
                            {t('saRating.history.to')}
                          </th>
                          <th scope="col" className="py-1 pr-3 font-medium">
                            {t('saRating.history.change')}
                          </th>
                          <th scope="col" className="py-1 font-medium">
                            {t('saRating.history.reason')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((a: RatingAdjustment) => (
                          <tr key={a.id} className="border-t border-white/10 align-top">
                            <td className="py-1.5 pr-3 whitespace-nowrap">
                              {formatDate(a.adjustedAt, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </td>
                            <td className="py-1.5 pr-3 font-mono">{String(a.oldElo)}</td>
                            <td className="py-1.5 pr-3 font-mono">{String(a.newElo)}</td>
                            <td className="py-1.5 pr-3 font-mono">
                              {a.change > 0 ? `+${String(a.change)}` : String(a.change)}
                            </td>
                            <td className="py-1.5">
                              {a.reason ?? (
                                <span className="text-ink-3">{t('saRating.history.noReason')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {canWrite ? (
                  <div className="mt-6 border-t border-white/10 pt-4" data-testid="sa-rating-form">
                    <h3 className="font-display text-sm font-semibold">
                      {t('saRating.form.title')}
                    </h3>
                    <p className="text-[11px] text-ink-3 mt-1">{t('saRating.form.lead')}</p>
                    <div className="mt-3 grid gap-3 md:grid-cols-[10rem_1fr]">
                      <Field
                        label={t('saRating.form.newRating')}
                        hint={t('saRating.form.levelUnchanged')}
                        error={
                          newElo.trim() !== '' && !eloValid
                            ? t('saRating.error.invalidRating')
                            : undefined
                        }
                      >
                        <input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          className="input w-full text-xs"
                          value={newElo}
                          onChange={(e) => setNewElo(e.target.value)}
                          data-testid="sa-rating-new"
                        />
                      </Field>
                      <Field
                        label={t('saRating.form.reason')}
                        hint={t('saRating.form.reasonHint', {
                          minimum: String(MIN_REASON_LENGTH),
                        })}
                      >
                        <textarea
                          className="input w-full text-xs"
                          rows={2}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          data-testid="sa-rating-reason"
                        />
                      </Field>
                    </div>
                    <div className="mt-3" data-testid="sa-rating-submit-row">
                      <ConfirmButton
                        className="btn-primary text-xs"
                        disabled={busy || !eloValid || !reasonValid}
                        question={t('saRating.form.confirm', {
                          name: selected.name,
                          from: selected.elo != null ? String(selected.elo) : '—',
                          to: eloValid ? String(parsedElo) : '—',
                        })}
                        confirmLabel={t('saRating.form.confirmYes')}
                        onConfirm={() => void submit()}
                      >
                        {busy ? t('saRating.form.saving') : t('saRating.form.submit')}
                      </ConfirmButton>
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-ink-3 mt-6" data-testid="sa-rating-readonly">
                    {t('saRating.form.readOnly')}
                  </p>
                )}

                {outcome && (
                  <div className="mt-3" data-testid="sa-rating-outcome">
                    <StatusMessage kind={outcome.kind}>{outcome.text}</StatusMessage>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
